/**
 * pauseMenu.ts — THE BACKPACK (Sorry Boss pause menu, BETA 22.54)
 *
 * Dylan's Lead-UI-Art-Director brief: "The pause menu IS your backpack."
 * A battered military pack splayed open with a cardboard panel strapped
 * inside — every element is a physical object: steel nav plates, a taped
 * polaroid of the survivor, run stats on a torn notepad sheet, the minimap
 * duct-taped to the bag, perks as graffiti-framed collectible cards, loot
 * scattered as stickers, and the angry duck telling you to get back to work.
 *
 * Art: 10-piece Nano Banana kit in /public/pause-kit (generated individually
 * in one shared style via scripts/gen-pause-assets.mjs, chroma-keyed offline).
 *
 * Rendering: the board art (1344×768) is COVER-fit to the canvas (no bars at
 * any aspect ratio); all content is laid out in BOARD-ART coordinates and
 * mapped through the same transform, so the collage rides the physical
 * cardboard on every screen. Far-right/left zones clamp inside the canvas.
 */

import type { GameState } from './types';
import { openBalancePanel } from './balancePanel';
import {
  getPercSlotDisplayData,
  getItemInventoryDisplayData,
  getXpBarData,
} from './itemsAdapter';
import {
  getWeaponAmmoDisplay,
  getWeaponSystemState,
} from './weaponAdapter';
import { getWeaponDef } from './weapons';
import { getCurrentFloor } from './enemyAdapter';
import { resolveAsset } from './uiAssets';
import { playMenuSound } from './audio';

// BETA 16.40 — engine.ts registers its minimap renderer via the setter below
// to avoid the circular import (engine already imports from pauseMenu).
type MinimapRenderer = (
  ctx: CanvasRenderingContext2D,
  state: GameState,
  w: number,
  h: number,
  overrideX: number,
  overrideY: number,
  overrideSize: number,
) => void;
let _minimapRenderer: MinimapRenderer | null = null;
export function registerPauseMinimapRenderer(fn: MinimapRenderer | null): void {
  _minimapRenderer = fn;
}

// ─── Palette (ink on cardboard / paint on steel) ─────────────────
const INK        = '#241d14';                 // marker on cardboard
const INK_DIM    = 'rgba(36,29,20,0.62)';
const PAPER_INK  = '#31302b';                 // pen on notepad paper
const BAG_TEXT   = '#ded6c2';                 // chalk-paint on the dark bag
const BAG_DIM    = 'rgba(222,214,194,0.55)';
const PLATE_TXT  = '#f0ece2';                 // stenciled paint on steel
const RED_PAINT  = '#b3922c';                 // (unused fallback)
const HP_RED     = '#a11c1c';
const GOOD_GREEN = '#3c6b2f';
const GOLD_AMBER = '#8a6a1d';

// ─── Menu model (same actions as the chalkboard — labels re-skinned) ─
const MENU_ITEMS = ['RESUME', 'BALANCING', 'SETTINGS', 'QUIT TO MENU'] as const;
const MENU_LABELS: Record<string, string> = {
  'RESUME': 'RESUME',
  'BALANCING': 'BALANCING',
  'SETTINGS': 'SETTINGS',
  'QUIT TO MENU': 'QUIT RUN',
};

// ─── State ───────────────────────────────────────────────────────
let hoveredIndex = -1;
let selectedIndex = 0;
// BETA 18.17 — exported so controllerAnomalies can detect "D-pad pressed
// in pause but selectedIndex didn't change" (PAUSE_INPUT_NO_NAV).
export function getPauseSelectedIndex(): number { return selectedIndex; }
let mouseX = 0;
let mouseY = 0;
let onQuitCallback: (() => void) | null = null;
let onSettingsCallback: (() => void) | null = null;
let _openedAt = 0;            // drives the backpack drop-in animation
let _lastHovered = -1;        // edge-detect for the plate-tap hover sound

// ─── The art kit (loaded once; render pops them in as they arrive) ─
function _kit(p: string): HTMLImageElement {
  const i = new Image();
  i.src = resolveAsset(`/pause-kit/${p}`);
  return i;
}
const boardImg   = _kit('board.png');
const titleImg   = _kit('title_paused.png');
const plateImg   = _kit('plate.png');
const plateRedImg = _kit('plate_red.png');
const photoImg   = _kit('photo_frame.png');
const notepadImg = _kit('notepad.png');
const stickyImg  = _kit('sticky.png');
const perkImg    = _kit('perk_frame.png');
const tapeImg    = _kit('tape.png');

// Lazy icon cache (perc/item/weapon sprites + hero portraits)
const _iconCache = new Map<string, HTMLImageElement>();
function icon(url: string): HTMLImageElement {
  let img = _iconCache.get(url);
  if (!img) {
    img = new Image();
    img.src = url.startsWith('http') ? url : resolveAsset(url);
    _iconCache.set(url, img);
  }
  return img;
}
const ready = (i: HTMLImageElement) => i.complete && i.naturalWidth > 0;

/** Reset selection to RESUME — call whenever the pause menu opens */
export function resetPauseMenu(): void {
  selectedIndex = 0;
  hoveredIndex = -1;
  _lastHovered = -1;
  _openedAt = performance.now();
  playMenuSound('ui_pack_open', 0.6);
}

/** Set the quit callback (called when QUIT TO MENU is selected) */
export function setPauseQuitCallback(cb: () => void) {
  onQuitCallback = cb;
}

/** Set the settings callback (called when SETTINGS is selected) */
export function setPauseSettingsCallback(cb: () => void) {
  onSettingsCallback = cb;
}

/** Update mouse position for hover detection */
export function updatePauseMousePos(x: number, y: number) {
  mouseX = x;
  mouseY = y;
}

// BETA 21.89 — resume to the pick screen we paused over (level-up / shrine /
// chest), if any, so pausing mid-pick doesn't skip the choice.
function _resumeToPhase(state: GameState): void {
  const prev = state._prePausePhase;
  state.phase = (prev as GameState['phase']) || 'playing';
  state._prePausePhase = undefined;
}

// ─── Board transform + layout (all deterministic from w,h) ──────
// board.png is 1344×768; the blank cardboard panel inside it was measured
// offline at (348,170)-(978,602) — content lives on/around that rect.
const ART_W = 1344, ART_H = 768;
const CB = { x: 348, y: 170, w: 630, h: 432 };

interface Xform { s: number; ox: number; oy: number; }
function boardXform(w: number, h: number): Xform {
  const s = Math.max(w / ART_W, h / ART_H);      // COVER — never any bars
  return { s, ox: (w - ART_W * s) / 2, oy: (h - ART_H * s) / 2 };
}
const mx = (t: Xform, ax: number) => t.ox + ax * t.s;
const my = (t: Xform, ay: number) => t.oy + ay * t.s;

// Nav plates — art-space slots on the cardboard's left column. Each art's
// visible STEEL BODY (measured offline, alpha-density bbox) is anchored to
// the slot rect; tape tabs / paint drips hang outside it naturally.
const PLATE = { x: 366, w: 190, y0: 258, gap: 86 };
// body fractions inside each art: {bx,by,bw,bh} + body aspect (h/w in art px)
const PLATE_GREY = { bx: 0.135, by: 0.10, bw: 0.855, bh: 0.83, aspect: 0.404 };
const PLATE_RED  = { bx: 0.034, by: 0.067, bw: 0.939, bh: 0.72, aspect: 0.521 };

function plateSlot(i: number) {
  const spec = MENU_ITEMS[i] === 'QUIT TO MENU' ? PLATE_RED : PLATE_GREY;
  return { x: PLATE.x, y: PLATE.y0 + i * PLATE.gap, w: PLATE.w, h: PLATE.w * spec.aspect, spec };
}

function plateRects(w: number, h: number) {
  const t = boardXform(w, h);
  return MENU_ITEMS.map((_, i) => {
    const sl = plateSlot(i);
    return { x: mx(t, sl.x), y: my(t, sl.y), w: sl.w * t.s, h: sl.h * t.s };
  });
}

function _activate(state: GameState, item: (typeof MENU_ITEMS)[number]): void {
  const _resumeUnmuffle = (): void => {
    import('./audio').then(a => a.endMuffledDuck());
  };
  if (item === 'RESUME') {
    playMenuSound('ui_pack_close', 0.55);
    _resumeToPhase(state);
    _resumeUnmuffle();
  } else if (item === 'BALANCING') {
    playMenuSound('ui_plate_slap', 0.5);
    openBalancePanel();
  } else if (item === 'SETTINGS') {
    playMenuSound('ui_plate_slap', 0.5);
    if (onSettingsCallback) onSettingsCallback();
  } else if (item === 'QUIT TO MENU') {
    playMenuSound('ui_pack_close', 0.6);
    _resumeUnmuffle();
    if (onQuitCallback) onQuitCallback();
  }
}

/** Handle click on pause menu — returns true if a button was clicked */
export function handlePauseClick(state: GameState, x: number, y: number, w: number, h: number): boolean {
  const rects = plateRects(w, h);
  for (let i = 0; i < rects.length; i++) {
    const r = rects[i];
    if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) {
      _activate(state, MENU_ITEMS[i]);
      return true;
    }
  }
  return false;
}

/** Handle keyboard input on pause menu */
export function handlePauseKeyDown(state: GameState, key: string): boolean {
  if (key === 'escape') {
    playMenuSound('ui_pack_close', 0.55);
    _resumeToPhase(state);
    import('./audio').then(a => a.endMuffledDuck());
    return true;
  }
  if (key === 'arrowup' || key === 'w') {
    selectedIndex = (selectedIndex - 1 + MENU_ITEMS.length) % MENU_ITEMS.length;
    playMenuSound('ui_plate_tap', 0.45);
    return true;
  }
  if (key === 'arrowdown' || key === 's') {
    selectedIndex = (selectedIndex + 1) % MENU_ITEMS.length;
    playMenuSound('ui_plate_tap', 0.45);
    return true;
  }
  if (key === 'enter' || key === ' ') {
    _activate(state, MENU_ITEMS[selectedIndex]);
    return true;
  }
  return false;
}

// ─── Animation helpers ───────────────────────────────────────────
function tSince(): number { return performance.now() - _openedAt; }
/** eased 0..1 progress of a window [start, start+dur] ms after open */
function anim(start: number, dur: number): number {
  const p = (tSince() - start) / dur;
  if (p <= 0) return 0;
  if (p >= 1) return 1;
  return 1 - Math.pow(1 - p, 3);            // easeOutCubic
}
function easeOutBack(p: number): number {
  const c1 = 1.20, c3 = c1 + 1;
  return 1 + c3 * Math.pow(p - 1, 3) + c1 * Math.pow(p - 1, 2);
}

// Deterministic tiny rotations so the collage looks hand-placed, not random
// per frame.
const rot = (deg: number) => (deg * Math.PI) / 180;

// ─── Render ──────────────────────────────────────────────────────
export function renderPauseMenu(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  w: number,
  h: number,
) {
  ctx.save();

  // ── Dark scrim over the frozen game ──
  const scrim = 0.78 * anim(0, 150);
  ctx.fillStyle = `rgba(6,5,4,${scrim.toFixed(3)})`;
  ctx.fillRect(0, 0, w, h);

  // ── The backpack drops in from above and settles ──
  const dropP = tSince() >= 320 ? 1 : easeOutBack(Math.max(0, tSince()) / 320);
  const t = boardXform(w, h);
  const dropY = (1 - dropP) * -0.09 * h;
  ctx.translate(0, dropY);

  if (ready(boardImg)) {
    ctx.drawImage(boardImg, t.ox, t.oy, ART_W * t.s, ART_H * t.s);
  } else {
    // kit still loading — plain cardboard stand-in so the menu is never blank
    ctx.fillStyle = '#8a7355';
    ctx.fillRect(mx(t, CB.x), my(t, CB.y), CB.w * t.s, CB.h * t.s);
  }

  const s = t.s;
  const fontPx = (n: number) => Math.max(9, Math.round(n * s));

  // ── Title: PAUSED stamps down over the cardboard top ──
  const titleP = anim(180, 220);
  if (titleP > 0 && ready(titleImg)) {
    const tw = 268 * s, th = tw * (titleImg.naturalHeight / titleImg.naturalWidth);
    const scale = 1.55 - 0.55 * titleP;
    ctx.save();
    ctx.globalAlpha = titleP;
    ctx.translate(mx(t, 663), my(t, 150) + th / 2);
    ctx.scale(scale, scale);
    ctx.drawImage(titleImg, -tw / 2, -th / 2, tw, th);
    ctx.restore();
  }

  // ── Nav plates (left column of the cardboard) ──
  const rects = plateRects(w, h);
  hoveredIndex = -1;
  for (let i = 0; i < rects.length; i++) {
    const r = rects[i];
    // hit-test against the un-dropped rect (dropY only during the intro beat)
    if (mouseX >= r.x && mouseX <= r.x + r.w && mouseY >= r.y - dropY && mouseY <= r.y + r.h - dropY) {
      hoveredIndex = i;
      selectedIndex = i;
    }
  }
  if (hoveredIndex !== _lastHovered) {
    if (hoveredIndex >= 0) playMenuSound('ui_plate_tap', 0.4);
    _lastHovered = hoveredIndex;
  }

  for (let i = 0; i < MENU_ITEMS.length; i++) {
    const item = MENU_ITEMS[i];
    const p = anim(230 + i * 55, 200);
    if (p <= 0) continue;
    const isRed = item === 'QUIT TO MENU';
    const img = isRed ? plateRedImg : plateImg;
    const sl = plateSlot(i);
    const active = i === hoveredIndex || i === selectedIndex;

    // slot rect in canvas px (with the entrance slide-in offset)
    const sx = mx(t, sl.x - (1 - p) * 36), sy = my(t, sl.y);
    const sw2 = sl.w * s, sh2 = sl.h * s;

    ctx.save();
    ctx.globalAlpha = p;
    ctx.translate(sx + sw2 / 2, sy + sh2 / 2);
    if (active) ctx.scale(1.045, 1.045);
    if (ready(img)) {
      // stretch the art so its measured BODY fills the slot exactly
      const dw = sw2 / sl.spec.bw;
      const dh = dw * (img.naturalHeight / img.naturalWidth);
      const dx = -sw2 / 2 - sl.spec.bx * dw;
      const dy = -sh2 / 2 - sl.spec.by * dh;
      if (active) ctx.filter = 'brightness(1.22)';
      ctx.drawImage(img, dx, dy, dw, dh);
      ctx.filter = 'none';
    } else {
      ctx.fillStyle = isRed ? '#5a1f1a' : '#5c6058';
      ctx.fillRect(-sw2 / 2, -sh2 / 2, sw2, sh2);
    }
    // Stenciled label centered on the steel body
    ctx.font = `${fontPx(22)}px Anton, Orbitron, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = 'rgba(0,0,0,0.45)';
    ctx.fillText(MENU_LABELS[item], 1.5 * s, 1.5 * s + 1);
    ctx.fillStyle = active ? '#ffffff' : PLATE_TXT;
    ctx.fillText(MENU_LABELS[item], 0, 1);
    // Selection scratch-mark
    if (i === selectedIndex) {
      ctx.fillStyle = active ? '#ffffff' : PLATE_TXT;
      ctx.font = `${fontPx(18)}px Anton, sans-serif`;
      ctx.fillText('▸', -sw2 / 2 - 10 * s, 1);
    }
    ctx.restore();
  }

  // ── Polaroid: the survivor, taped up crooked ──
  const photoP = anim(320, 260);
  if (photoP > 0) {
    const pw = 130 * s, ph = pw * (photoImg.naturalHeight ? photoImg.naturalHeight / photoImg.naturalWidth : 1.31);
    ctx.save();
    ctx.globalAlpha = photoP;
    ctx.translate(mx(t, 600) + pw / 2, my(t, 300) + ph / 2);
    ctx.rotate(rot(-2.5 - (1 - photoP) * 5));
    // frame FIRST — its picture area is painted solid black in the art, so
    // the mugshot must go ON TOP, clipped to the window.
    if (ready(photoImg)) ctx.drawImage(photoImg, -pw / 2, -ph / 2, pw, ph);
    const heroId = state.character?.id;
    if (heroId) {
      const hero = icon(`/characters/hero/${heroId}.png`);
      if (ready(hero)) {
        const win = { x: 0.115 * pw, y: 0.17 * ph, w: 0.72 * pw, h: 0.565 * ph };
        ctx.save();
        ctx.beginPath();
        ctx.rect(-pw / 2 + win.x, -ph / 2 + win.y, win.w, win.h);
        ctx.clip();
        // top ~55% of the hero art = head + torso (mugshot crop)
        const srcH = hero.naturalHeight * 0.55;
        const scale = Math.max(win.w / hero.naturalWidth, win.h / srcH);
        const dw = hero.naturalWidth * scale, dh = srcH * scale;
        ctx.drawImage(hero, 0, 0, hero.naturalWidth, srcH,
          -pw / 2 + win.x + (win.w - dw) / 2, -ph / 2 + win.y + (win.h - dh) / 2 + 4 * s, dw, dh);
        ctx.restore();
      }
    }
    // name scrawled on the white strip
    ctx.font = `${fontPx(15)}px Anton, sans-serif`;
    ctx.fillStyle = PAPER_INK;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText((state.character?.name || '???').toUpperCase(), -0.02 * pw, ph * 0.355);
    ctx.restore();
  }

  // ── Notepad: run stats in pen ──
  const padP = anim(380, 260);
  if (padP > 0) {
    const nw = 178 * s, nh = nw * (notepadImg.naturalHeight ? notepadImg.naturalHeight / notepadImg.naturalWidth : 1.35);
    const nx = mx(t, 756), ny = my(t, 285);
    ctx.save();
    ctx.globalAlpha = padP;
    ctx.translate(nx + nw / 2, ny + nh / 2);
    ctx.rotate(rot(1.4));
    if (ready(notepadImg)) ctx.drawImage(notepadImg, -nw / 2, -nh / 2, nw, nh);
    else { ctx.fillStyle = '#e8e2cc'; ctx.fillRect(-nw / 2, -nh / 2, nw, nh); }

    const elapsed = Math.floor(state.time);
    const mins = Math.floor(elapsed / 60);
    const secs = elapsed % 60;
    const xp = getXpBarData();
    const hpPct = state.player.hp / state.player.maxHp;
    const rows: Array<[string, string, string]> = [
      ['TIME',  `${mins}:${secs.toString().padStart(2, '0')}`, PAPER_INK],
      ['FLOOR', `${getCurrentFloor()}`, PAPER_INK],
      ['LEVEL', `${state.player.level}`, PAPER_INK],
      ['HP',    `${Math.ceil(state.player.hp)}/${Math.round(state.player.maxHp)}`, hpPct > 0.5 ? GOOD_GREEN : hpPct > 0.25 ? GOLD_AMBER : HP_RED],
      ['GOLD',  `${Math.floor(state.player.gold)}`, GOLD_AMBER],
      ['KILLS', `${state.killCount}`, HP_RED],
      ['XP',    `${Math.floor(xp.currentXp)}/${Math.round(xp.xpToNextLevel)}`, PAPER_INK],
    ];
    ctx.textBaseline = 'middle';
    const rx0 = -nw / 2 + nw * 0.17, rx1 = nw / 2 - nw * 0.12;
    let ry = -nh / 2 + nh * 0.175;
    const rGap = nh * 0.096;
    for (const [label, value, color] of rows) {
      ctx.font = `${fontPx(12)}px "Share Tech Mono", monospace`;
      ctx.fillStyle = INK_DIM;
      ctx.textAlign = 'left';
      ctx.fillText(label, rx0, ry);
      ctx.font = `bold ${fontPx(13)}px "Share Tech Mono", monospace`;
      ctx.fillStyle = color;
      ctx.textAlign = 'right';
      ctx.fillText(value, rx1, ry);
      ry += rGap;
    }
    ctx.restore();
  }

  // Tooltip state — collected while drawing rows, drawn last
  let tooltipDesc = '';
  let ttX = 0, ttY = 0;

  // ── Loot: item sprites scattered in a grid under the photo ──
  const lootP = anim(460, 240);
  if (lootP > 0) {
    ctx.save();
    ctx.globalAlpha = lootP;
    ctx.font = `${fontPx(14)}px Anton, sans-serif`;
    ctx.fillStyle = INK;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText('LOOT', mx(t, 598), my(t, 512));
    const items = getItemInventoryDisplayData();
    if (items.length === 0) {
      ctx.font = `${fontPx(11)}px "Share Tech Mono", monospace`;
      ctx.fillStyle = INK_DIM;
      ctx.fillText('nothing yet…', mx(t, 598), my(t, 532));
    } else {
      const cell = 27 * s, per = 5;
      items.slice(0, 15).forEach((item, idx) => {
        const gx = mx(t, 600) + (idx % per) * cell;
        const gy = my(t, 520) + Math.floor(idx / per) * cell;
        const ic = item.iconUrl ? icon(item.iconUrl) : null;
        ctx.save();
        ctx.translate(gx + cell / 2, gy + cell / 2);
        ctx.rotate(rot(((idx * 37) % 11) - 5));      // hand-scattered look
        if (ic && ready(ic)) ctx.drawImage(ic, -cell * 0.44, -cell * 0.44, cell * 0.88, cell * 0.88);
        else { ctx.fillStyle = 'rgba(36,29,20,0.25)'; ctx.fillRect(-cell * 0.4, -cell * 0.4, cell * 0.8, cell * 0.8); }
        ctx.restore();
        if (item.stacks > 1) {
          ctx.font = `bold ${fontPx(9)}px "Share Tech Mono", monospace`;
          ctx.fillStyle = INK;
          ctx.textAlign = 'right';
          ctx.fillText(`x${item.stacks}`, gx + cell - 1, gy + cell - 2);
          ctx.textAlign = 'left';
        }
        if (mouseX >= gx && mouseX < gx + cell && mouseY - dropY >= gy && mouseY - dropY < gy + cell && item.description) {
          tooltipDesc = `${item.name} — ${item.description}`;
          ttX = gx; ttY = gy + cell + 4;
        }
      });
      if (items.length > 15) {
        ctx.font = `${fontPx(10)}px "Share Tech Mono", monospace`;
        ctx.fillStyle = INK_DIM;
        ctx.fillText(`+${items.length - 15} more`, mx(t, 600), my(t, 520) + 3 * 27 * s + 12 * s);
      }
    }
    ctx.restore();
  }

  // ── Weapons: side-profile sprites taped to the cardboard bottom ──
  const wepP = anim(520, 240);
  if (wepP > 0) {
    ctx.save();
    ctx.globalAlpha = wepP;
    const weaponState = getWeaponSystemState();
    const weps = weaponState?.weapons ?? [];
    let wy = my(t, 528);
    const wxIcon = mx(t, 758), wxText = mx(t, 826);
    for (let i = 0; i < Math.min(weps.length, 2); i++) {
      const wep = weps[i];
      const def = getWeaponDef(wep.definitionId);
      const spr = def?.spriteUrl ? icon(def.spriteUrl) : null;
      if (spr && ready(spr)) {
        // contain-fit the side-profile sprite in a 58×30 mount box
        const fit = Math.min((58 * s) / spr.naturalWidth, (30 * s) / spr.naturalHeight);
        const sw = spr.naturalWidth * fit, sh = spr.naturalHeight * fit;
        ctx.save();
        ctx.translate(wxIcon + 29 * s, wy + 14 * s);
        ctx.rotate(rot(i === 0 ? -4 : 3));
        ctx.drawImage(spr, -sw / 2, -sh / 2, sw, sh);
        // small tape bit over the sprite mount
        if (ready(tapeImg)) {
          ctx.globalAlpha = wepP * 0.9;
          ctx.drawImage(tapeImg, 0, 0, tapeImg.naturalWidth, tapeImg.naturalHeight * 0.5,
            -14 * s, -16 * s, 28 * s, 12 * s);
        }
        ctx.restore();
      }
      ctx.font = `${fontPx(12)}px Anton, sans-serif`;
      ctx.fillStyle = INK;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText((def?.name ?? wep.definitionId).toUpperCase(), wxText, wy + 4 * s);
      // Lv + ammo UNDER the name (right-aligning put it on the spray can art)
      ctx.font = `${fontPx(10)}px "Share Tech Mono", monospace`;
      ctx.fillStyle = INK_DIM;
      ctx.fillText(`Lv${wep.level}${i === 0 ? `  ·  ${getWeaponAmmoDisplay()}` : ''}`, wxText, wy + 17 * s);
      wy += 36 * s;
    }
    ctx.restore();
  }

  // ── Minimap: duct-taped to the bag, right of the cardboard ──
  const mapP = anim(430, 260);
  if (mapP > 0 && _minimapRenderer) {
    const mapSize = Math.min(226 * s, h * 0.34);
    const mapX = Math.min(mx(t, 996), w - mapSize - 10);
    const mapY = my(t, 198);
    ctx.save();
    ctx.globalAlpha = mapP;
    _minimapRenderer(ctx, state, w, h, mapX, mapY, mapSize);
    // two torn tape strips over opposite corners
    if (ready(tapeImg)) {
      const tw2 = tapeImg.naturalWidth, th2 = tapeImg.naturalHeight;
      ctx.save();
      ctx.translate(mapX + 6, mapY + 4);
      ctx.rotate(rot(-38));
      ctx.drawImage(tapeImg, 0, 0, tw2, th2 * 0.5, -34 * s, -8 * s, 68 * s, 20 * s);
      ctx.restore();
      ctx.save();
      ctx.translate(mapX + mapSize - 6, mapY + mapSize - 4);
      ctx.rotate(rot(-38));
      ctx.drawImage(tapeImg, 0, th2 * 0.5, tw2, th2 * 0.5, -34 * s, -8 * s, 68 * s, 20 * s);
      ctx.restore();
    }
    ctx.font = `${fontPx(11)}px "Share Tech Mono", monospace`;
    ctx.fillStyle = BAG_DIM;
    ctx.textAlign = 'center';
    ctx.fillText('you = red arrow', mapX + mapSize / 2, mapY + mapSize + 14 * s);
    ctx.restore();
  }

  // ── Sticky note: unlocks this run (only when there are any) ──
  const history = state.runUnlocksHistory ?? [];
  const stickyP = anim(560, 240);
  if (stickyP > 0 && history.length > 0) {
    const sw = 150 * s, sh = sw * (stickyImg.naturalHeight ? stickyImg.naturalHeight / stickyImg.naturalWidth : 1.13);
    const sx = Math.min(mx(t, 1002), w - sw - 10), sy = my(t, 452);
    ctx.save();
    ctx.globalAlpha = stickyP;
    ctx.translate(sx + sw / 2, sy + sh / 2);
    ctx.rotate(rot(2.2));
    if (ready(stickyImg)) ctx.drawImage(stickyImg, -sw / 2, -sh / 2, sw, sh);
    ctx.font = `${fontPx(11)}px Anton, sans-serif`;
    ctx.fillStyle = PAPER_INK;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText('UNLOCKED:', -sw * 0.32, -sh * 0.22);
    ctx.font = `${fontPx(10)}px "Share Tech Mono", monospace`;
    history.slice(0, 5).forEach((u, i2) => {
      ctx.fillText(`✓ ${u.label.slice(0, 20)}`, -sw * 0.32, -sh * 0.08 + i2 * 13 * s);
    });
    ctx.restore();
  }

  // ── Perk cards: graffiti frames, bottom-right on the bag ──
  const perkP = anim(600, 260);
  if (perkP > 0) {
    const percs = getPercSlotDisplayData();
    if (percs.length > 0) {
      const card = 68 * s, gap = 10 * s, per = 4;
      const zoneW = per * card + (per - 1) * gap;
      const px0 = Math.min(mx(t, 998), w - zoneW - 12);
      const py0 = my(t, 636);
      ctx.save();
      ctx.globalAlpha = perkP;
      ctx.font = `${fontPx(13)}px Anton, sans-serif`;
      ctx.fillStyle = BAG_TEXT;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText('PERKS', px0, py0 - 6 * s);
      percs.slice(0, 8).forEach((slot, idx) => {
        const gx = px0 + (idx % per) * (card + gap);
        const gy = py0 + Math.floor(idx / per) * (card + gap + 8 * s);
        ctx.save();
        ctx.translate(gx + card / 2, gy + card / 2);
        ctx.rotate(rot(((idx * 53) % 7) - 3));
        const ic = slot.percIcon ? icon(slot.percIcon) : null;
        if (ic && ready(ic)) ctx.drawImage(ic, -card * 0.36, -card * 0.36, card * 0.72, card * 0.72);
        if (ready(perkImg)) ctx.drawImage(perkImg, -card / 2, -card / 2, card, card);
        // level chip
        ctx.font = `bold ${fontPx(10)}px "Share Tech Mono", monospace`;
        ctx.fillStyle = '#0c0a08';
        ctx.fillRect(card * 0.16, card * 0.3, card * 0.34, card * 0.2);
        ctx.fillStyle = '#c9a4e8';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`L${slot.level}`, card * 0.33, card * 0.4);
        ctx.restore();
        if (mouseX >= gx && mouseX < gx + card && mouseY - dropY >= gy && mouseY - dropY < gy + card && slot.description) {
          tooltipDesc = `${slot.percName} Lv${slot.level}/${slot.maxLevel} ${slot.totalBonusDisplay} — ${slot.description}`;
          ttX = gx - 80 * s; ttY = gy - 8 * s;
        }
      });
      ctx.restore();
    }
  }

  // ── The survivor rises into the bottom-left corner (Dylan 2026-07-02:
  // duck sticker removed; the character's POSE art — top half only — pops
  // up from the screen edge, like the approved level-up mockup). Poses live
  // at /characters/poses/<id>.png (greg first; more coming from Dylan).
  const poseP = anim(680, 300);
  const poseId = state.character?.id;
  if (poseP > 0 && poseId) {
    const pose = icon(`/characters/poses/${poseId}.png`);
    if (ready(pose)) {
      const srcH = pose.naturalHeight * 0.62;          // top half, whole thumbs-up kept
      const drawH = h * 0.40 * (0.85 + 0.15 * poseP);  // pops up as it lands
      const drawW = drawH * (pose.naturalWidth / srcH);
      const px = Math.max(mx(t, 60), 10);
      const py = h - drawH * poseP - dropY;            // rises from the bottom edge
      ctx.save();
      ctx.globalAlpha = Math.min(1, poseP * 1.4);
      ctx.shadowColor = 'rgba(0,0,0,0.55)';            // real-lighting drop shadow
      ctx.shadowBlur = 22;
      ctx.shadowOffsetX = 14;
      ctx.shadowOffsetY = 8;
      ctx.drawImage(pose, 0, 0, pose.naturalWidth, srcH, px, py, drawW, drawH);
      ctx.restore();
    }
  }

  // ── Tooltip: torn masking-tape label ──
  if (tooltipDesc) {
    ctx.font = `${fontPx(11)}px "Share Tech Mono", monospace`;
    const maxW = 240 * s;
    const words = tooltipDesc.split(' ');
    const lines: string[] = [];
    let cur = '';
    for (const word of words) {
      const test = cur ? cur + ' ' + word : word;
      if (ctx.measureText(test).width > maxW - 16) { lines.push(cur); cur = word; }
      else cur = test;
    }
    if (cur) lines.push(cur);
    const boxW = Math.min(maxW, Math.max(...lines.map(l => ctx.measureText(l).width)) + 16);
    const boxH = lines.length * 14 * s + 12;
    let bx = Math.min(ttX, w - boxW - 8);
    let by = ttY;
    if (by + boxH > h - 8) by = ttY - boxH - 34 * s;
    ctx.save();
    ctx.translate(bx + boxW / 2, by + boxH / 2);
    ctx.rotate(rot(-1.2));
    ctx.fillStyle = 'rgba(216,209,188,0.96)';
    ctx.fillRect(-boxW / 2, -boxH / 2, boxW, boxH);
    ctx.strokeStyle = 'rgba(36,29,20,0.6)';
    ctx.lineWidth = 1;
    ctx.strokeRect(-boxW / 2, -boxH / 2, boxW, boxH);
    ctx.fillStyle = PAPER_INK;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    lines.forEach((line, i) => {
      ctx.fillText(line, -boxW / 2 + 8, -boxH / 2 + 7 + (i + 0.5) * 14 * s);
    });
    ctx.restore();
  }

  // ── Footer hint ──
  ctx.font = `${fontPx(11)}px "Share Tech Mono", monospace`;
  ctx.fillStyle = 'rgba(222,214,194,0.4)';
  ctx.textAlign = 'center';
  ctx.fillText('ESC to resume  ·  ↑↓ to navigate  ·  Enter to select', w / 2, h - 12 - dropY);

  ctx.restore();
}
